import React, { useState } from 'react'
import { Card, Dropdown, Input, Menu, Tooltip } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import {
  BsBookmarkHeart,
  BsBookmarkHeartFill,
  BsFolderX,
  BsFolderFill,
} from 'react-icons/bs'
import { MdDashboard } from 'react-icons/md'
import { RiDashboardLine } from 'react-icons/ri'
import {
  HiPresentationChartBar,
  HiOutlinePresentationChartBar,
} from 'react-icons/hi'
import { BiSort } from 'react-icons/bi'
import { useRoles } from 'hooks'
import { config } from 'configs'
import { Can } from 'permissions'
import { InsufficientPermissions } from 'screens/errors'
import {
  ComponentViewHeader,
  getComponentCurrentView,
  getComponentInitialValues,
  getComponentOptions,
  getComponentSubHeading,
} from './components'

const ComponentView = ({
  component,
  heading,
  optionList,
  createButtonLink,
  setSearchText,
  sortFilterData,
  sortKey,
  setSortKey,
  createTapButtonLink,
  type,
  extraButtons = [],
}) => {
  const optionKeys = Object.keys(optionList)
  const initialValue = getComponentInitialValues(optionKeys)
  const { isAll, isMy, isInactive, isFavorite, isPresent } =
    getComponentOptions(optionKeys)
  const { isPresenter } = useRoles()
  const [isViews, setViews] = useState(initialValue)
  const changeView = (key, value) => setViews({ ...initialValue, [key]: value })
  const currentView = getComponentCurrentView(isViews)

  const sortFilterMenu = (
    <Menu>
      {sortFilterData
        ?.filter((item) => item?.showOn?.includes(currentView))
        .map((item, index) => (
          <Menu.Item
            key={index}
            onClick={() => setSortKey(item.value)}
            style={
              sortKey === item.value && {
                backgroundColor: config.colors.primary,
                color: 'white',
              }
            }
          >
            {item.label}
          </Menu.Item>
        ))}
    </Menu>
  )

  const listTobeRendered =
    optionKeys.find((key) => isViews[key] === true) ?? 'my'

  const myOptionSelector = (
    <>
      {isViews.my ? (
        <Tooltip title={`See All ${heading}`}>
          <MdDashboard
            fontSize="1.7rem"
            id="events-my-clicked"
            className="t-text-secondary-100 t-cursor-pointer"
            onClick={() => changeView('my', false)}
          />
        </Tooltip>
      ) : (
        <Tooltip title={`See My ${heading}`}>
          <RiDashboardLine
            fontSize="1.7rem"
            id="events-my-not-clicked"
            className="t-text-secondary-100 t-cursor-pointer"
            onClick={() => changeView('my', true)}
          />
        </Tooltip>
      )}
    </>
  )
  const inactiveOptionSelector = (
    <>
      {isViews.inactive ? (
        <Tooltip title={`See Active ${heading}`}>
          <BsFolderFill
            fontSize="1.7rem"
            id="events-inactive-clicked"
            className="t-text-secondary-100 t-cursor-pointer"
            onClick={() => changeView('inactive', false)}
          />
        </Tooltip>
      ) : (
        <Tooltip title={`See Inactive ${heading}`}>
          <BsFolderX
            fontSize="1.7rem"
            id="events-inactive-not-clicked"
            className="t-text-secondary-100 t-cursor-pointer"
            onClick={() => changeView('inactive', true)}
          />
        </Tooltip>
      )}
    </>
  )
  return (
    <Card
      bordered={false}
      className="header-solid mb-24"
      title={
        <div className="t-flex t-flex-wrap t-justify-between">
          <ComponentViewHeader
            component={component}
            heading={heading}
            subHeading={getComponentSubHeading(isViews, heading)}
            buttonLink={createButtonLink}
            createTapButtonLink={createTapButtonLink}
            type={type}
            extraButtons={extraButtons}
          />
          <div className="t-flex t-justify-end t-items-center t-space-x-2 t-mt-4 sm:t-mt-0">
            <Can I={config.actions.read} a={component}>
              {() => (
                <>
                  <div>
                    <>
                      {sortFilterData &&
                        sortFilterData?.length > 0 &&
                        sortFilterMenu?.props?.children?.length > 0 && (
                          <Dropdown
                            overlay={sortFilterMenu}
                            placement="bottomLeft"
                            arrow
                          >
                            <Tooltip title="Sort Filter">
                              <BiSort
                                fontSize="1.7rem"
                                className="t-text-secondary-100 t-cursor-pointer"
                              />
                            </Tooltip>
                          </Dropdown>
                        )}
                    </>
                  </div>
                  <div className="header-control t-mt-0">
                    <Input
                      id="searchEvent"
                      className="header-search t-w-36"
                      placeholder="Search ..."
                      prefix={<SearchOutlined />}
                      onChange={(e) => {
                        setSearchText(e.target.value)
                      }}
                    />
                  </div>
                </>
              )}
            </Can>

            {/* If present is in options */}
            {isPresent && isPresenter && (
              <>
                {isViews.presenter ? (
                  <Tooltip title={`See All ${heading}`}>
                    <HiPresentationChartBar
                      fontSize="1.7rem"
                      id="events-presenter-clicked"
                      className="t-text-secondary-100 t-cursor-pointer"
                      onClick={() => changeView('presenter', false)}
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title={`Presenter ${heading}`}>
                    <HiOutlinePresentationChartBar
                      fontSize="1.7rem"
                      id="events-presenter-not-clicked"
                      className="t-text-secondary-100 t-cursor-pointer"
                      onClick={() => changeView('presenter', true)}
                    />
                  </Tooltip>
                )}
              </>
            )}

            {/* If my is in options */}
            {isAll &&
              isMy &&
              (component ? (
                <Can I={config.actions.read} a={component}>
                  {myOptionSelector}
                </Can>
              ) : (
                myOptionSelector
              ))}

            {/* If inactive is in options */}
            {isInactive &&
              (component ? (
                <Can I={config.actions.read} a={component}>
                  {inactiveOptionSelector}
                </Can>
              ) : (
                inactiveOptionSelector
              ))}

            {/* If favorite is in options */}
            {isFavorite && (
              <>
                {isViews.favorite ? (
                  <Tooltip title={`See All ${heading}`} placement="topRight">
                    <BsBookmarkHeartFill
                      fontSize="1.7rem"
                      id="events-favourite-clicked"
                      className="t-text-secondary-100 t-cursor-pointer"
                      onClick={() => changeView('favorite', false)}
                    />
                  </Tooltip>
                ) : (
                  <Tooltip
                    title={`See Favorite ${heading}`}
                    placement="topRight"
                  >
                    <BsBookmarkHeart
                      fontSize="1.7rem"
                      id="events-favourite-not-clicked"
                      className="t-text-secondary-100 t-cursor-pointer"
                      onClick={() => changeView('favorite', true)}
                    />
                  </Tooltip>
                )}
              </>
            )}
          </div>
        </div>
      }
    >
      <div style={{ minHeight: 'calc(100vh - 205px)' }}>
        {component ? (
          <Can I={config.actions.read} a={component} passThrough>
            {(allowed) =>
              allowed ? (
                optionList[listTobeRendered]
              ) : (
                <InsufficientPermissions showCard={false} />
              )
            }
          </Can>
        ) : (
          optionList[listTobeRendered]
        )}
      </div>
    </Card>
  )
}

export { ComponentView }
